import React, { useState, useRef } from "react";
import {
  Typography,
  Box,
  Grid,
  Button,
  Drawer,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import tw from "twin.macro";

// Import all section components
import NavigationTree from "components/PrivacyPolicy/NavigationTree";
import PersonalInfoPolicy from "components/PrivacyPolicy/PersonalInfoPolicy";
import PrivacyMark from "components/PrivacyPolicy/PrivacyMark";
import InfoPurpose from "components/PrivacyPolicy/InfoPurpose";
import PersonalDataNotice from "components/PrivacyPolicy/PersonalDataNotice";
import SpecificPersonalInfo from "components/PrivacyPolicy/SpecificPersonalInfo";
import SpecificHandling from "components/PrivacyPolicy/SpecificHandling";
import GlobalStyle from "helpers/GlobalStyle";
import SplashAnimationContainer from "helpers/SplashAnimationContainer";
import useScreenSize from "helpers/useScreenSize";
import Footer from "components/footers/FiveColumnDark";
import Hero from "components/hero/Hero";
import BgTitle from "components/title/BgTitle";

// Japanese-inspired container with appropriate spacing
const Container = tw.div`relative mx-auto md:px-8 py-6 max-w-screen-xl`;

const PrivacyPolicyPage = () => {
  const { isXs } = useScreenSize();
  const contentRef = useRef(null);

  const [selectedSection, setSelectedSection] = useState(
    "personal-info-policy"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:900px)");

  const handleSectionSelect = (sectionId) => {
    setSelectedSection(sectionId);
    if (isSmallScreen) {
      setMobileOpen(false);
    }

    // Find the element and scroll to it
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetPosition = element.offsetTop - 80; // Adjust for any fixed headers
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div style={{ marginTop: "50px" }}>
      <GlobalStyle />
      <Hero isXs={isXs} />

      <SplashAnimationContainer>
        <Container>
          <BgTitle text={"個人情報の取扱いについて"} />
          <Box sx={{ position: "relative", mt: 2 }}>
            <Grid container spacing={3}>
              {/* Navigation Sidebar - Desktop */}
              {!isSmallScreen && (
                <Grid item xs={12} md={3} lg={3}>
                  <Box sx={{ marginBottom: 3 }}>
                    <NavigationTree
                      selectedSection={selectedSection}
                      onSelectSection={handleSectionSelect}
                    />
                  </Box>
                </Grid>
              )}

              {/* Mobile Drawer & Toggle Button */}
              {isSmallScreen && (
                <>
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setMobileOpen(true)}
                      startIcon={<i className="fas fa-list"></i>}
                      sx={{
                        py: 1.5,
                        borderColor: "#0c4d96",
                        color: "#0c4d96",
                        "&:hover": {
                          borderColor: "#083b75",
                          backgroundColor: "rgba(12, 77, 150, 0.04)",
                        },
                      }}
                    >
                      目次を開く
                    </Button>
                  </Grid>
                  <Drawer
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    sx={{
                      "& .MuiDrawer-paper": {
                        width: "85%",
                        maxWidth: 300,
                        boxSizing: "border-box",
                        borderTopRightRadius: 8,
                        borderBottomRightRadius: 8,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        目次
                      </Typography>
                      <IconButton
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <NavigationTree
                        selectedSection={selectedSection}
                        onSelectSection={handleSectionSelect}
                        isMobile={true}
                      />
                    </Box>
                  </Drawer>
                </>
              )}

              {/* Content Area */}
              <Grid item xs={12} md={9} lg={9}>
                <Box ref={contentRef}>
                  {/* Section Components */}
                  <PersonalInfoPolicy isXs={isXs} id="personal-info-policy" />
                  <PrivacyMark isXs={isXs} id="privacy-mark" />
                  <InfoPurpose isXs={isXs} id="info-purpose" />
                  <PersonalDataNotice isXs={isXs} id="personal-data-notice" />
                  <SpecificPersonalInfo
                    isXs={isXs}
                    id="specific-personal-info"
                  />
                  <SpecificHandling isXs={isXs} id="specific-handling" />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
        <Footer isXs={isXs} />
      </SplashAnimationContainer>
    </div>
  );
};

export default PrivacyPolicyPage;
